import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ActivityIndicator,
	NativeScrollEvent,
	NativeSyntheticEvent,
	Platform,
	ScrollView,
	StyleSheet,
	Text,
	TextInput,
	TextStyle,
	View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import { IconCloseSvg } from '../../../assets/inventory';
import { blockPlayer, getBlockStatus, unblockPlayer } from '../../../services/blocksApi';
import { CHAT_MESSAGE_MAX_LENGTH } from '../../../services/chatApi';
import { RootState } from '../../../store';
import {
	chatClosed,
	chatConversationsRequest,
	chatMarkReadRequest,
	chatMessagesRequest,
	chatOpened,
	chatResolveRequest,
	chatSendRequest,
} from '../../../store/chat/chatActions';
import {
	ChatMessageView,
	selectChatConversations,
	selectChatConversationWithPlayer,
	selectChatResolution,
	selectChatThread,
} from '../../../store/chat/chatReducer';
import { markSeenRequest } from '../../../store/userNotifications/notificationsActions';
import { selectNotifications } from '../../../store/userNotifications/notificationsReducer';
import { chatPlayerIdFromUrl } from '../../../utils/helpers/chatLinks';
import { showPlainToast } from '../../../utils/helpers/toastUtils';
import { GradientFill } from '../common/GradientFill';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { Pressable } from '../common/Touchable';
import RedesignModal from '../Modals/RedesignModal';
import { useRedesignScale } from '../Modals/useRedesignScale';
import ChatAvatar from './ChatAvatar';
import { CHAT_EMOJIS } from './chatEmojis';
import { chatDayKey, formatChatDay, formatChatTime } from './chatFormat';
import {
	CHAT_FONT as FONT,
	CHAT_LIST_FILL,
	CHAT_PANEL as PANEL,
	CHAT_PANEL_FILL,
	CHAT_PANEL_RIM,
	chatColors as colors,
} from './chatTheme';

type Props = {
	/** The other player. */
	playerId: string;
	/** Shown until the conversation itself names them, and in the locked and empty states. */
	username?: string;
	onClose: () => void;
};

/** `outlineStyle` is React Native Web's; the cast is how the rest of the app spells it. */
type WebInputStyle = TextStyle & { outlineStyle?: 'none' };

/** The counter under the field shows once the message gets this close to the cap. */
const COUNTER_FROM = CHAT_MESSAGE_MAX_LENGTH - 100;
/** Within this distance of the top the next older page loads; of the bottom, new messages follow. */
const LOAD_OLDER_EDGE = 60;
const STICK_TO_BOTTOM_EDGE = 48;

type Row =
	| { kind: 'day'; key: string; label: string }
	| { kind: 'message'; key: string; message: ChatMessageView };

const newClientId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const SendIcon = ({ size, color }: { size: number; color: string }) => (
	<Svg width={size} height={size} viewBox='0 0 24 24'>
		<Path
			d='M3.4 11.1 19.9 4.2c.7-.3 1.4.4 1.1 1.1l-6.9 16.5c-.3.8-1.5.7-1.7-.1l-1.6-6.4-6.4-1.6c-.8-.2-.9-1.4-.1-1.7Z'
			fill={color}
		/>
	</Svg>
);

/**
 * The 1:1 chat a donation unlocks, opened for a player id. It finds the conversation the two
 * players share; until a donation has unlocked one it says how to, and has no input.
 *
 * The thread runs oldest at the top. Scrolling to the top loads the page before the oldest loaded
 * message; while the player is at the bottom, new messages keep it there. Our own messages show at
 * once as pending bubbles and can be retried in place when the send fails. The conversation is
 * marked read on open and again as messages arrive while it is open.
 *
 * Put together from the inbox's pieces (there is no Figma frame): the red panel, the sunken list,
 * and gold-rimmed plates for our own bubbles.
 */
export const PrivateChatModal = ({ playerId, username, onClose }: Props) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);

	const myId = useSelector((s: RootState) => (s.auth?.user?.id ? String(s.auth.user.id) : ''));
	const resolution = useSelector((s: RootState) => selectChatResolution(s, playerId));
	const conversation = useSelector((s: RootState) => selectChatConversationWithPlayer(s, playerId));
	const conversations = useSelector(selectChatConversations);
	const notifications = useSelector(selectNotifications);

	const conversationId = resolution?.conversationId ?? conversation?.id ?? null;
	const unlocked = !!conversationId && (resolution ? resolution.unlocked : true);
	const resolving = !conversationId && (!resolution || resolution.loading);
	const thread = useSelector((s: RootState) => selectChatThread(s, conversationId));

	const otherName = conversation?.otherPlayer.username || username || '';
	const shownName = otherName || t('T_SITE_CHAT_THIS_PLAYER', { defaultValue: 'this player' });

	const blockedText = t('T_SITE_CHAT_SEND_FORBIDDEN', {
		defaultValue: 'You can no longer send messages to this user.',
	});

	const [text, setText] = useState('');
	const [emojiOpen, setEmojiOpen] = useState(false);

	// --- Blocking ----------------------------------------------------------------------------------

	// Whether we blocked them; null until known, and then the header offers nothing. The API never
	// says whether they blocked us: that shows when a send is refused.
	const [blockedByMe, setBlockedByMe] = useState<boolean | null>(null);
	const [confirmingBlock, setConfirmingBlock] = useState(false);
	const [blockBusy, setBlockBusy] = useState(false);

	useEffect(() => {
		if (!myId) return;
		let current = true;
		setBlockedByMe(null);
		getBlockStatus(playerId)
			.then(status => {
				if (current) setBlockedByMe(status.blockedByMe);
			})
			.catch(error => {
				console.warn('[PrivateChat] block status unavailable', error?.message);
			});
		return () => {
			current = false;
		};
	}, [myId, playerId]);

	const setBlocked = async (block: boolean) => {
		setBlockBusy(true);
		try {
			const status = block ? await blockPlayer(playerId) : await unblockPlayer(playerId);
			setBlockedByMe(status.blockedByMe);
			setConfirmingBlock(false);
			if (status.blockedByMe) setEmojiOpen(false);
			showPlainToast(
				'success',
				block
					? t('T_SITE_BLOCK_DONE', { defaultValue: 'Player blocked' })
					: t('T_SITE_BLOCK_UNBLOCK_DONE', { defaultValue: 'Player unblocked' }),
			);
		} catch {
			showPlainToast(
				'error',
				t('T_SITE_BLOCK_FAILED', { defaultValue: 'Something went wrong. Please try again.' }),
			);
		} finally {
			setBlockBusy(false);
		}
	};

	// Blocking asks first, as on the profile; unblocking does not.
	const onBlockPress = () => {
		if (blockBusy) return;
		if (blockedByMe) setBlocked(false);
		else setConfirmingBlock(true);
	};
	const selection = useRef({ start: 0, end: 0 });
	const inputRef = useRef<TextInput>(null);
	const sendRef = useRef<View>(null);

	// --- Loading -----------------------------------------------------------------------------------

	useEffect(() => {
		if (!myId) return;
		dispatch(chatResolveRequest({ playerId }));
		// Only the list carries the other player's name and picture.
		if (!conversations.loaded && !conversations.loading) dispatch(chatConversationsRequest());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, myId, playerId]);

	useEffect(() => {
		if (!conversationId || !unlocked) return;
		dispatch(chatOpened({ conversationId }));
		// Always the newest page on open: the socket may have missed messages while the chat was shut.
		dispatch(chatMessagesRequest({ conversationId }));
		return () => {
			dispatch(chatClosed({ conversationId }));
		};
	}, [dispatch, conversationId, unlocked]);

	// --- Read state --------------------------------------------------------------------------------

	const lastIncomingId = useMemo(() => {
		for (let i = thread.items.length - 1; i >= 0; i--) {
			const message = thread.items[i];
			if (!message.status && message.senderId !== myId) return message.id;
		}
		return null;
	}, [thread.items, myId]);

	useEffect(() => {
		if (!conversationId || !unlocked) return;
		dispatch(chatMarkReadRequest({ conversationId }));
	}, [dispatch, conversationId, unlocked, lastIncomingId]);

	// The inbox's "new message from ..." entries for this chat are read along with it.
	const seenNotificationIds = useRef(new Set<string>());
	useEffect(() => {
		if (!conversationId || !unlocked) return;
		for (const notification of notifications as Array<{
			id: string;
			read?: boolean;
			url?: string;
		}>) {
			if (notification.read || seenNotificationIds.current.has(notification.id)) continue;
			if (chatPlayerIdFromUrl(notification.url) !== playerId) continue;
			seenNotificationIds.current.add(notification.id);
			dispatch(markSeenRequest({ notificationId: notification.id }));
		}
	}, [dispatch, notifications, conversationId, unlocked, playerId]);

	// --- Scrolling ---------------------------------------------------------------------------------

	const scrollRef = useRef<ScrollView>(null);
	const metrics = useRef({ contentHeight: 0, y: 0 });
	const stickToBottom = useRef(true);
	/** Where the content stood when an older page was asked for, to keep the view still as it lands. */
	const anchor = useRef<{ height: number; y: number } | null>(null);

	const loadOlder = () => {
		if (!conversationId || !thread.loaded || !thread.hasMore || thread.loadingOlder) return;
		if (anchor.current) return;
		anchor.current = { height: metrics.current.contentHeight, y: metrics.current.y };
		dispatch(chatMessagesRequest({ conversationId, older: true }));
	};

	const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
		const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
		metrics.current.y = contentOffset.y;
		stickToBottom.current =
			contentSize.height - (contentOffset.y + layoutMeasurement.height) <
			STICK_TO_BOTTOM_EDGE * scale;
		if (contentOffset.y < LOAD_OLDER_EDGE * scale) loadOlder();
	};

	const onContentSizeChange = (_width: number, height: number) => {
		const previous = metrics.current.contentHeight;
		metrics.current.contentHeight = height;

		if (anchor.current) {
			if (height !== anchor.current.height) {
				const y = anchor.current.y + (height - anchor.current.height);
				anchor.current = null;
				scrollRef.current?.scrollTo({ x: 0, y, animated: false });
			}
			return;
		}
		if (stickToBottom.current) scrollRef.current?.scrollToEnd({ animated: previous > 0 });
	};

	// An older page that failed or came back empty leaves the content as it was: drop the anchor.
	useEffect(() => {
		if (!thread.loadingOlder && anchor.current?.height === metrics.current.contentHeight) {
			anchor.current = null;
		}
	}, [thread.loadingOlder]);

	// --- Composing ---------------------------------------------------------------------------------

	const trimmed = text.trim();
	const canSend = !!conversationId && unlocked && !!myId && !blockedByMe && trimmed.length > 0;

	const send = () => {
		if (!canSend || !conversationId) return;
		stickToBottom.current = true;
		dispatch(
			chatSendRequest({
				conversationId,
				clientId: newClientId(),
				text: trimmed.slice(0, CHAT_MESSAGE_MAX_LENGTH),
				senderId: myId,
				recipientId: playerId,
			}),
		);
		setText('');
		selection.current = { start: 0, end: 0 };
	};

	const retry = (message: ChatMessageView) => {
		if (!conversationId || !message.clientId) return;
		stickToBottom.current = true;
		dispatch(
			chatSendRequest({
				conversationId,
				clientId: message.clientId,
				text: message.text,
				senderId: myId,
				recipientId: playerId,
			}),
		);
	};

	const insertEmoji = (emoji: string) => {
		if (text.length + emoji.length > CHAT_MESSAGE_MAX_LENGTH) return;
		const { start, end } = selection.current;
		const at = Math.min(start, text.length);
		const next = `${text.slice(0, at)}${emoji}${text.slice(Math.min(end, text.length))}`;
		setText(next);
		selection.current = { start: at + emoji.length, end: at + emoji.length };
	};

	const toggleEmoji = () => {
		setEmojiOpen(open => {
			// The grid takes the keyboard's place rather than stacking over it.
			if (!open) inputRef.current?.blur();
			return !open;
		});
	};

	// On the web, Enter sends and Shift+Enter breaks the line, as in any desktop chat.
	const onKeyPress = (event: any) => {
		if (Platform.OS !== 'web') return;
		const { key, shiftKey } = event?.nativeEvent ?? {};
		if (key !== 'Enter' || shiftKey) return;
		event.preventDefault?.();
		send();
	};

	// --- Rows --------------------------------------------------------------------------------------

	const rows = useMemo(() => {
		const out: Row[] = [];
		let lastDay = '';
		for (const message of thread.items) {
			const day = chatDayKey(message.createdAt);
			if (day && day !== lastDay) {
				out.push({ kind: 'day', key: `day-${day}`, label: formatChatDay(message.createdAt, t) });
				lastDay = day;
			}
			out.push({ kind: 'message', key: message.clientId ?? message.id, message });
		}
		return out;
	}, [thread.items, t]);

	const renderMessage = (message: ChatMessageView) => {
		const mine = message.senderId === myId;
		const failed = message.status === 'failed';
		// A send the chat refused (a block) says so, with nothing to retry; any other failure can be.
		const retryable = failed && message.retryable !== false;
		const sending = message.status === 'sending';
		return (
			<View style={[styles.messageRow, mine ? styles.messageRowMine : styles.messageRowTheirs]}>
				<Pressable
					accessibilityRole={retryable ? 'button' : undefined}
					accessibilityLabel={
						retryable ? t('T_SITE_CHAT_RETRY', { defaultValue: 'Tap to retry' }) : undefined
					}
					disabled={!retryable}
					onPress={() => retry(message)}
					style={[
						styles.bubble,
						mine ? styles.bubbleMine : styles.bubbleTheirs,
						sending && styles.bubbleSending,
						failed && styles.bubbleFailed,
					]}
				>
					<Text style={[styles.bubbleText, mine && styles.bubbleTextMine]}>{message.text}</Text>
					<Text style={styles.bubbleStamp}>
						{sending
							? t('T_SITE_CHAT_SENDING', { defaultValue: 'Sending…' })
							: formatChatTime(message.createdAt)}
					</Text>
				</Pressable>
				{retryable ? (
					<Pressable accessibilityRole='button' onPress={() => retry(message)} hitSlop={6}>
						<Text style={styles.failedText}>
							{message.error ?? t('T_SITE_CHAT_SEND_FAILED', { defaultValue: 'Message not sent.' })}{' '}
							<Text style={styles.retryText}>
								{t('T_SITE_CHAT_RETRY', { defaultValue: 'Tap to retry' })}
							</Text>
						</Text>
					</Pressable>
				) : failed ? (
					<Text style={styles.failedText}>{message.error ?? blockedText}</Text>
				) : null}
			</View>
		);
	};

	const retryLoad = () => {
		if (conversationId && unlocked) dispatch(chatMessagesRequest({ conversationId }));
		else dispatch(chatResolveRequest({ playerId }));
	};

	const errorKey = unlocked ? thread.errorKey : resolution?.errorKey;
	const centred = (content: React.ReactNode) => <View style={styles.statusBox}>{content}</View>;

	let body: React.ReactNode;
	if (!myId || resolving || (unlocked && !thread.loaded && !thread.errorKey)) {
		body = centred(<ActivityIndicator color={colors.gold300} />);
	} else if (errorKey && (!unlocked || !thread.loaded)) {
		body = centred(
			<View style={styles.statusStack}>
				<Text style={styles.status}>{errorKey}</Text>
				<Pressable accessibilityRole='button' onPress={retryLoad} style={styles.retryButton}>
					<Text style={styles.retryButtonText}>
						{t('T_SITE_CHAT_TRY_AGAIN', { defaultValue: 'Try again' })}
					</Text>
				</Pressable>
			</View>,
		);
	} else if (!unlocked) {
		body = centred(
			<View style={styles.statusStack}>
				<ChatAvatar
					avatarFileId={conversation?.otherPlayer.avatarFileId}
					size={64 * scale}
					scale={scale}
				/>
				<Text style={styles.status}>
					{t('T_SITE_CHAT_LOCKED', {
						defaultValue: 'Donate to {{username}} to unlock a private chat.',
						username: shownName,
					})}
				</Text>
			</View>,
		);
	} else if (!rows.length) {
		body = centred(
			<View style={styles.statusStack}>
				<ChatAvatar
					avatarFileId={conversation?.otherPlayer.avatarFileId}
					size={64 * scale}
					scale={scale}
				/>
				<Text style={styles.status}>
					{t('T_SITE_CHAT_EMPTY', {
						defaultValue: 'Say hello to {{username}}!',
						username: shownName,
					})}
				</Text>
			</View>,
		);
	} else {
		body = (
			<IndicatorScrollView
				ref={scrollRef}
				contentContainerStyle={styles.thread}
				indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
				onScroll={onScroll}
				scrollEventThrottle={16}
				onContentSizeChange={onContentSizeChange}
				keyboardShouldPersistTaps='handled'
			>
				{rows.map(row =>
					row.kind === 'day' ? (
						<View key={row.key} style={styles.dayRow}>
							<Text style={styles.dayLabel}>{row.label}</Text>
						</View>
					) : (
						<React.Fragment key={row.key}>{renderMessage(row.message)}</React.Fragment>
					),
				)}
			</IndicatorScrollView>
		);
	}

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			keyboardSubmitRef={sendRef}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				fill: CHAT_PANEL_FILL,
				rim: CHAT_PANEL_RIM,
				overScreen: true,
			}}
		>
			<View style={styles.body}>
				<View style={styles.titleBar}>
					<ChatAvatar
						avatarFileId={conversation?.otherPlayer.avatarFileId}
						size={40 * scale}
						scale={scale}
					/>
					<Text style={styles.title} numberOfLines={1}>
						{otherName}
					</Text>
					{blockedByMe !== null ? (
						<Pressable
							accessibilityRole='button'
							accessibilityState={{ busy: blockBusy }}
							onPress={onBlockPress}
							hitSlop={6}
							style={[styles.blockButton, blockBusy && styles.blockButtonBusy]}
						>
							<Text numberOfLines={1} style={styles.blockButtonText}>
								{blockedByMe
									? t('T_SITE_BLOCK_UNBLOCK_PLAYER', { defaultValue: 'Unblock player' })
									: t('T_SITE_BLOCK_PLAYER', { defaultValue: 'Block player' })}
							</Text>
						</Pressable>
					) : null}
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_CLOSE_CHAT', { defaultValue: 'Close chat' })}
						onPress={onClose}
						hitSlop={8}
						style={styles.close}
					>
						<IconCloseSvg width={24 * scale} height={24 * scale} />
					</Pressable>
				</View>

				<View style={styles.list}>
					<GradientFill stops={CHAT_LIST_FILL} />
					{body}
					{thread.loadingOlder ? (
						<View pointerEvents='none' style={styles.olderSpinner}>
							<ActivityIndicator size='small' color={colors.gold300} />
						</View>
					) : null}
					{confirmingBlock ? (
						<View style={styles.confirmOverlay}>
							<View style={styles.confirmBox}>
								<Text style={styles.confirmText}>
									{t('T_SITE_BLOCK_CONFIRM', {
										defaultValue:
											"Block {{username}}? They won't be able to message you or send you donations, and you won't be able to message or donate to them.",
										username: shownName,
									})}
								</Text>
								<View style={styles.confirmActions}>
									<Pressable
										accessibilityRole='button'
										onPress={blockBusy ? undefined : () => setConfirmingBlock(false)}
										style={styles.confirmButton}
									>
										<Text numberOfLines={1} style={styles.confirmCancelText}>
											{t('T_Cancel', { defaultValue: 'Cancel' })}
										</Text>
									</Pressable>
									<Pressable
										accessibilityRole='button'
										accessibilityState={{ busy: blockBusy }}
										onPress={blockBusy ? undefined : () => setBlocked(true)}
										style={[styles.confirmButton, styles.confirmDanger]}
									>
										<Text numberOfLines={1} style={styles.confirmDangerText}>
											{t('T_SITE_BLOCK_CONFIRM_BUTTON', { defaultValue: 'Block' })}
										</Text>
									</Pressable>
								</View>
							</View>
						</View>
					) : null}
				</View>

				{unlocked && conversationId && blockedByMe ? (
					<Text style={styles.blockedNote}>{blockedText}</Text>
				) : unlocked && conversationId ? (
					<View style={styles.composer}>
						{emojiOpen ? (
							<View style={styles.emojiGrid}>
								{CHAT_EMOJIS.map(emoji => (
									<Pressable
										key={emoji}
										accessibilityRole='button'
										accessibilityLabel={emoji}
										onPress={() => insertEmoji(emoji)}
										style={styles.emojiCell}
									>
										<Text style={styles.emoji}>{emoji}</Text>
									</Pressable>
								))}
							</View>
						) : null}

						<View style={styles.inputRow}>
							<Pressable
								accessibilityRole='button'
								accessibilityLabel={t('T_SITE_A11Y_CHAT_EMOJI', { defaultValue: 'Emoji' })}
								accessibilityState={{ expanded: emojiOpen }}
								onPress={toggleEmoji}
								style={[styles.iconButton, emojiOpen && styles.iconButtonActive]}
							>
								<Text style={styles.emojiToggle}>😊</Text>
							</Pressable>
							<View style={styles.inputBox}>
								<TextInput
									ref={inputRef}
									value={text}
									onChangeText={setText}
									onSelectionChange={event => {
										selection.current = event.nativeEvent.selection;
									}}
									onFocus={() => setEmojiOpen(false)}
									onKeyPress={onKeyPress}
									placeholder={t('T_SITE_CHAT_PLACEHOLDER', { defaultValue: 'Write a message…' })}
									placeholderTextColor={colors.muted}
									maxLength={CHAT_MESSAGE_MAX_LENGTH}
									multiline
									style={styles.input}
								/>
							</View>
							<Pressable
								ref={sendRef}
								accessibilityRole='button'
								accessibilityLabel={t('T_SITE_A11Y_CHAT_SEND', { defaultValue: 'Send message' })}
								disabled={!canSend}
								onPress={send}
								style={[styles.iconButton, styles.sendButton, !canSend && styles.sendDisabled]}
							>
								<SendIcon size={20 * scale} color={colors.sunken} />
							</Pressable>
						</View>
						{text.length >= COUNTER_FROM ? (
							<Text style={styles.counter}>
								{text.length}/{CHAT_MESSAGE_MAX_LENGTH}
							</Text>
						) : null}
					</View>
				) : null}
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const rim = Math.max(1, scale);
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(PANEL.gap),
		},
		titleBar: { flexDirection: 'row', alignItems: 'center', gap: s(10) },
		title: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(20),
			lineHeight: s(20 * 1.3),
			color: colors.gold200,
		},
		close: { width: s(32), height: s(32), alignItems: 'center', justifyContent: 'center' },
		list: {
			flex: 1,
			minHeight: 0,
			paddingHorizontal: s(6),
			paddingVertical: s(8),
			borderRadius: s(12),
			borderWidth: rim,
			borderColor: colors.listRim,
			overflow: 'hidden',
		},
		// 10 on the right keeps the bubbles clear of the scroll bar.
		thread: { gap: s(6), paddingTop: s(2), paddingRight: s(10), paddingBottom: s(4) },
		olderSpinner: {
			position: 'absolute',
			top: s(8),
			left: 0,
			right: 0,
			alignItems: 'center',
		},
		dayRow: { alignItems: 'center', paddingVertical: s(4) },
		dayLabel: {
			overflow: 'hidden',
			paddingHorizontal: s(10),
			paddingVertical: s(2),
			borderRadius: s(10),
			borderWidth: rim,
			borderColor: colors.borderSubtle,
			backgroundColor: colors.sunken,
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(11),
			lineHeight: s(14),
			color: colors.muted,
		},
		messageRow: { gap: s(2) },
		messageRowMine: { alignItems: 'flex-end' },
		messageRowTheirs: { alignItems: 'flex-start' },
		bubble: {
			maxWidth: '80%',
			paddingHorizontal: s(10),
			paddingTop: s(7),
			paddingBottom: s(5),
			borderRadius: s(12),
			borderWidth: rim,
			gap: s(2),
		},
		bubbleMine: {
			borderBottomRightRadius: s(4),
			borderColor: colors.buttonRim,
			backgroundColor: colors.mineFill,
		},
		bubbleTheirs: {
			borderBottomLeftRadius: s(4),
			borderColor: colors.borderSubtle,
			backgroundColor: colors.theirsFill,
		},
		bubbleSending: { opacity: 0.6 },
		bubbleFailed: { borderColor: colors.failed },
		bubbleText: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(14),
			lineHeight: s(19),
			color: colors.secondary,
		},
		bubbleTextMine: { color: colors.gold50 },
		bubbleStamp: {
			alignSelf: 'flex-end',
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(10),
			lineHeight: s(12),
			color: colors.muted,
		},
		failedText: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(11),
			lineHeight: s(14),
			color: colors.failed,
			textAlign: 'right',
		},
		retryText: { color: colors.gold200, textDecorationLine: 'underline' },
		// Header: a solid red pill next to the close button, so it reads against the red header.
		blockButton: {
			paddingHorizontal: s(14),
			paddingVertical: s(7),
			borderRadius: s(14),
			borderWidth: rim,
			borderColor: '#FFB3AB',
			backgroundColor: '#E0352B',
		},
		blockButtonBusy: { opacity: 0.5 },
		blockButtonText: {
			fontFamily: FONT,
			fontWeight: '800',
			fontSize: s(13),
			lineHeight: s(16),
			color: '#FFFFFF',
		},
		// In place of the input while we have blocked them.
		blockedNote: {
			paddingVertical: s(10),
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(13),
			lineHeight: s(17),
			color: colors.muted,
			textAlign: 'center',
		},
		confirmOverlay: {
			...StyleSheet.absoluteFillObject,
			alignItems: 'center',
			justifyContent: 'center',
			padding: s(16),
			backgroundColor: 'rgba(15, 3, 1, 0.78)',
		},
		confirmBox: {
			gap: s(14),
			padding: s(16),
			borderRadius: s(12),
			borderWidth: rim,
			borderColor: colors.buttonRim,
			backgroundColor: colors.sunken,
		},
		confirmText: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(14),
			lineHeight: s(19),
			color: colors.gold50,
			textAlign: 'center',
		},
		confirmActions: { flexDirection: 'row', justifyContent: 'center', gap: s(10) },
		confirmButton: {
			minWidth: s(96),
			alignItems: 'center',
			paddingHorizontal: s(14),
			paddingVertical: s(8),
			borderRadius: s(10),
			borderWidth: rim,
			borderColor: colors.borderSubtle,
			backgroundColor: colors.theirsFill,
		},
		confirmDanger: { borderColor: colors.failed, backgroundColor: '#5A0B03' },
		confirmCancelText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(14),
			lineHeight: s(18),
			color: colors.gold100,
		},
		confirmDangerText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(14),
			lineHeight: s(18),
			color: '#FFFFFF',
		},
		statusBox: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: s(16) },
		statusStack: { alignItems: 'center', gap: s(12) },
		status: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(14),
			lineHeight: s(18),
			color: colors.muted,
			textAlign: 'center',
		},
		retryButton: {
			paddingHorizontal: s(14),
			paddingVertical: s(6),
			borderRadius: s(10),
			borderWidth: rim,
			borderColor: colors.buttonRim,
			backgroundColor: colors.mineFill,
		},
		retryButtonText: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(13),
			lineHeight: s(16),
			color: colors.gold100,
		},
		composer: { gap: s(6) },
		emojiGrid: {
			flexDirection: 'row',
			flexWrap: 'wrap',
			justifyContent: 'center',
			paddingVertical: s(4),
			borderRadius: s(12),
			borderWidth: rim,
			borderColor: colors.listRim,
			backgroundColor: colors.sunken,
		},
		// Eight to a row on the 334 grid: 40 emoji in five rows.
		emojiCell: { width: s(40), height: s(38), alignItems: 'center', justifyContent: 'center' },
		emoji: { fontSize: s(24), lineHeight: s(30) },
		// The emoji and send buttons stay level with the middle of the box as it grows.
		inputRow: { flexDirection: 'row', alignItems: 'center', gap: s(8) },
		iconButton: {
			width: s(40),
			height: s(40),
			borderRadius: s(20),
			borderWidth: rim,
			borderColor: colors.borderSubtle,
			backgroundColor: colors.sunken,
			alignItems: 'center',
			justifyContent: 'center',
		},
		iconButtonActive: { borderColor: colors.buttonRim, backgroundColor: colors.mineFill },
		emojiToggle: { fontSize: s(20), lineHeight: s(24) },
		inputBox: {
			flex: 1,
			minHeight: s(40),
			justifyContent: 'center',
			paddingHorizontal: s(12),
			paddingVertical: s(6),
			borderRadius: s(20),
			borderWidth: rim,
			borderColor: colors.borderSubtle,
			backgroundColor: colors.sunken,
		},
		input: {
			maxHeight: s(96),
			padding: 0,
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(14),
			lineHeight: s(19),
			color: colors.gold50,
			textAlignVertical: 'center',
			outlineStyle: 'none',
		} as WebInputStyle,
		sendButton: { borderColor: colors.buttonRim, backgroundColor: colors.gold300 },
		sendDisabled: { opacity: 0.45 },
		counter: {
			alignSelf: 'flex-end',
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(11),
			lineHeight: s(13),
			color: colors.muted,
		},
	});
};

export default PrivateChatModal;
